import { initializeApp, getApps } from "firebase/app";
import {
  getAuth, GoogleAuthProvider,
  createUserWithEmailAndPassword, updateProfile,
  sendEmailVerification, sendPasswordResetEmail,
} from "firebase/auth";

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY            as string,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN        as string,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID         as string,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET     as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID             as string,
  measurementId:     import.meta.env.VITE_FIREBASE_MEASUREMENT_ID     as string,
};

// Guard against double-init in HMR environments
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

/**
 * Returns the base origin to use in Firebase continueUrl / action-link URLs.
 * Firebase authorized domains only accept root domains (no www.*), so we strip
 * any www. prefix.  VITE_APP_URL takes priority so production can override.
 */
function getFirebaseAppOrigin(): string {
  const configured = (import.meta.env.VITE_APP_URL as string | undefined)?.replace(/\/$/, "");
  if (configured) return configured;
  // Strip www. so localhost:3000 and orchy.site are used (both are authorized),
  // while www.orchy.site (which Firebase rejects) is never sent as a continueUrl.
  return window.location.origin.replace(/^(https?:\/\/)www\./, "$1");
}

/** Creates a Firebase account, sets display name, sends a verification email. */
export async function createFirebaseAccount(email: string, password: string, displayName: string) {
  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(user, { displayName });
  const continueUrl = `${getFirebaseAppOrigin()}/auth/verify-email`;
  await sendEmailVerification(user, { url: continueUrl });
  return user;
}

/** Reloads the current Firebase user and returns whether their email is verified. */
export async function checkEmailVerified(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  await user.reload();
  return auth.currentUser?.emailVerified ?? false;
}

// continueUrl lands the user on our in-app reset page where they enter a new password
export const sendFirebasePasswordReset = (email: string) =>
  sendPasswordResetEmail(auth, email, {
    url: `${getFirebaseAppOrigin()}/auth/reset-password`,
    handleCodeInApp: true,
  });
